<template>
  <div class="category-selector is-flex">
    <div>
      <div class="dropdown-trigger" :class="{'cursor-pointer': !exceededMaxCategories}" @keypress.enter.space="openCategorySelectModal" @click="openCategorySelectModal">
        <div class="is-flex" tabindex="0">
          <div class="category-button has-text-grey is-flex is-unselectable">
            <span v-if="showCategoryButton" class="icon plus-btn" qa="add-cat-btn" tabindex="0">
              <i v-if="!showCategories" class="fal fa-plus-circle has-text-primary" aria-hidden="true"></i>
              <i v-else class="fal fa-times" aria-hidden="true"></i>
            </span>
            <span class="is-uppercase">{{ $t("projectPage.categories") }}</span>
          </div>
        </div>
      </div>
      <div v-if="showCategories" class="modal is-active">
        <div @click="closeCategorySelectModal" class="modal-background"></div>
        <aside class="modal-card is-flex" qa="category-selector-modal">
          <span @click="closeCategorySelectModal" class="close cursor-pointer"><i class="fal fa-times"></i></span>
          <header v-if="$isYSC" class="title">
            <h2>{{ $t('category.title') }}</h2>
          </header>
          <section class="categories-container has-tooltips">
            <div v-for="(category, index) in allCategories" :key="category.id" class="category">
              <div class="is-flex">
                <div class="category-image-container"><img class="category-image" :src="category.image" :alt="`Category ${category.name}`"/></div>
                <span class="cursor-pointer is-flex">
                  <div class="category-title" @click="addCategory(category)">
                    {{ category.name.toUpperCase() }}
                  </div>
                  <span class="icon data-balloon cursor-pointer" :data-balloon="category.description" :data-balloon-pos="dataBalloonPos(index)" data-balloon-length="large">
                    <i class="tooltip-icon has-text-primary fas fa-info"></i>
                  </span>
                </span>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </div>
    <div v-for="(category, offset) in chosenCategories" :class="{'is-unselectable' : true, 'has-categories' : chosenCategories && chosenCategories.length, 'is-profile' : isProfile, 'big-tag': isModal}" :key="offset" class="tag" :data-category="category" :data-offset="offset" draggable="true" @dragstart="drag" @dragover="allowDrop" @drop="drop">
      {{projectCategoriesNames(category)}}
      <button class="delete is-small" tabindex="0" @keypress.enter.space="removeCategory(category)" @click="removeCategory(category)"></button>
    </div>
    <div v-if="exceededMaxCategories" class="help has-text-grey">{{error}}</div>
  </div>
</template>

<script>
export default {
  props: ['value', 'isProfile', 'includeSubCategories', 'isAdminMenu', 'isModal', 'fairID', 'showAllCategories', 'project'],
  data: function () {
    return {
      showCategories: false,
      dragging: { to: null, from: null },
      isFocusedInput: false,
      chosenCategories: this.value.slice(),
      error: '',
      showError: false,
      showLoadingModal: false
    }
  },
  watch: {
    chosenCategories: function () {
      this.$emit('input', this.chosenCategories)
    },
    value: function () {
      if (this.value && this.value.length !== this.chosenCategories.length) {
        this.chosenCategories = this.value.slice()
      }
    }
  },
  mounted: function () {
    // prevent click outside event with popupItem. Always add it in mounted according documentation https://github.com/vue-bulma/click-outside#readme -vueclick-outside
    this.popupItem = this.$el
    // set error message if no more categories can be selected
    if (this.exceededMaxCategories) {
      this.error = 'You have reached your category selection limit. Remove a category if you would like to add a new selection.'
    }
  },
  computed: {
    allCategories: function () {
      return this.$store.getters.categoriesLoaded ? this.$store.getters.sortedProjectCategories(this.includeSubCategories) : []
    },
    maxCategorySelection: function () {
      return this.$isYSC || this.$isAjas || this.$isIsef || this.$isOhioScience ? 1 : 3
    },
    exceededMaxCategories: function () {
      return this.chosenCategories.length >= this.maxCategorySelection
    },
    showCategoryButton: function () {
      return !this.exceededMaxCategories && !this.hasFairID
    },
    hasFairID: function () {
      return this.$isIsef && this.fairID !== ''
    }
  },
  created: function () {
    if (this.$route.params.fairSlug) {
      this.$store.dispatch('setPage', this.$route.params.fairSlug)
    } else if (this.$isYSC || this.$isOasam) {
      const fair = this.$store.getters.fairs[this.project.id]
      if (fair) {
        this.$store.dispatch('setPage', fair.explore_path)
      } else {
        this.$store.dispatch('getFairForTemplate', this.project.sourceProjectID).then(fair => {
          this.$store.dispatch('setPage', fair.explore_path)
          this.$store.dispatch('getCategories')
        })
      }
    }
    this.$store.dispatch('getCategories')
  },
  methods: {
    canSelectCategory: function () {
      return this.chosenCategories.length >= this.maxCategorySelection
    },
    addCategory: function (category) {
      if (this.exceededMaxCategories) {
        this.error = 'You have reached your category selection limit. Remove a category if you would like to add a new selection.'
        return
      }
      const newCategoryName = category.name
      const matchedTags = this.chosenCategories.filter((category) => { return category.name === newCategoryName })
      if (matchedTags.length !== 0) {
        return
      }
      this.chosenCategories.push(category)
      this.showCategories = false
      this.$emit('calculateBodyHeight')
    },
    removeCategory: function (category) {
      this.chosenCategories.splice(this.chosenCategories.indexOf(category), 1)
    },
    drag: function (event) {
      this.dragging.from = parseInt(event.target.dataset.offset)
    },
    canDrop: function (to) {
      return !isNaN(to) && this.dragging.from !== null && this.dragging.from !== to
    },
    allowDrop: function (event) {
      const to = parseInt(event.target.dataset.offset)
      if (this.canDrop(to)) {
        event.preventDefault()
        this.dragging.to = to
        this.drop(event)
      }
    },
    drop: function (event) {
      event.preventDefault()
      this.chosenCategories.splice(this.dragging.to, 0, this.chosenCategories.splice(this.dragging.from, 1)[0])
      this.dragging.from = this.dragging.to
      this.dragging.to = null
    },
    closeCategorySelectModal: function () {
      this.showCategories = false
    },
    openCategorySelectModal: function () {
      if (!this.exceededMaxCategories) {
        this.showCategories = true
      }
    },
    projectCategoriesNames: function (category) {
      return category?.name && category.name !== '' ? category.name : this.$store.getters.nameCategoryByID(category.id)
    },
    dataBalloonPos: function (index) {
      let pos = 'down-right'
      if (index > this.allCategories.length - 3) {
        pos = 'up-right'
      }
      return pos
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';
  $border-color: #DCDCDC;
  $modal-body-color: #F2F2F2;
  ::v-deep .modal-card {
    justify-content: unset !important;
  }
  .category-selector {
    width: initial;
    flex-wrap: wrap;
    align-items: center;
    .category-button {
      margin: 0.5rem 0;
      margin-right: 0.5rem;
      font-size: 0.7rem;
      align-items: center;
      .icon {
        margin-right: 0.5rem;
        font-size: 1.25rem;
      }
    }
  }
  .tag {
    font-size: 0.6rem;
    color: white;
    border-radius: 0;
    margin-right: .5em;
    &.big-tag {
      font-size: 0.8rem;
      margin-bottom: 0.5rem;
    }
  }
  .is-profile {
    .tag {
      font-size: 0.8rem;
    }
  }
  .dropdown-item {
    padding-left: 0.25rem;
    font-size: 0.8rem;
    line-height: 1rem;
    &.subcategory {
      padding-left: 0.5rem;
    }
  }
  .dropdown-content {
    min-height: 27vh;
    max-height: 40vh;
    overflow-y: auto;
    @include mobile {
      max-height: 30vh;
    }
    div {
      font-weight: bold;
      padding-left: 0.25rem;
      line-height: 1.5rem;
      font-size: 0.8rem;
    }
  }
  .admin-toolbar { // class is applied externally
    .dropdown {
      margin-bottom: 0.3rem;
    }
    .dropdown-trigger {
      margin-right: 0;
    }
    .tag {
      margin: 0.15rem 0.3rem 0.15rem 0;
    }
  }
  .plus-btn {
    i {
      font-size: 1.5rem;
    }
  }
  .close {
    font-size: 1.3rem;
    background-color: transparent;
    opacity: 0.75;
    position: absolute;
    right: 1rem;
    top: 0.5rem;
    border: none;
  }

  .ysc-modal-card {
    background: white !important;
    max-height: 700px !important;
    max-width: 640px !important;
    overflow-x: hidden;
  @include mobile-and-ipad {
    overflow: visible;
  }
  }

  .modal-card {
    overflow-y: hidden;
    overflow: auto;
    background: $modal-body-color;
    flex-direction: column;
    justify-content: space-between;
    max-width: 620px;
    width: 98vw;
    max-height: 640px;
    height: calc(100vh - 20px);
  @include mobile {
    max-height: 80%;
  }
  img {
    display: block;
  }
  }

  .category-image-container {
    min-width: 26px;
    min-height: 26px;
  }
  .category-image {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    @include shadow;
  }

  /*// HEADER styles*/
 .title {
   border-bottom: 1px solid $border-color;
   margin-bottom: 0 !important;
   text-align: center;
    h2 {
      font-family: $primary-font;
      font-weight: $primary-font-weight;
      font-size: 1.5rem;
      line-height: 2.2rem;
      padding: 1.6rem 3rem 1.4rem;
    }
    @include mobile {
      font-size: 1.2rem;
      padding: 1.5rem 0 1rem;
      .mobile-line-break {
        display: block;
        padding-top: 2px;
      }
    }
  }
  .categories-container {
    overflow: visible !important;
    padding-top: 1rem;
    margin: auto;
    @include touch {
      overflow-y: scroll !important;
    }
  }

  .category {
    margin: 1.25rem auto 1rem auto;
    padding: 0 1rem;
  }

  .category-title {
    font-weight: 700;
    margin: 0 0 0.95rem 0.5rem;
    &:hover {
     filter: brightness(200%);
    }
    &:active {
     filter: brightness(100%);
    }
  }

  .subcategories-container {
    margin-left: 2rem;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .subcategory{
    display: flex;
    justify-content: center;
    align-content: center;
    flex-direction: column;
    font-size: 0.8rem;
    background-color: white;
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
    padding: 0.25rem 0.75rem;
    border: 1px solid transparent;
  &.selected {
     background-color: $modal-body-color;
     border-color: black;
   }
  }

  .select-all {
    text-decoration: underline;
    margin-left: 0.5rem;
    font-size: 0.91rem;
  }

.tooltip-icon {
  font-size: 0.75rem;
}
</style>
